import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  createProductCategory,
  deleteProductCategory,
  fetchProductCategories,
  updateProductCategory,
} from '../../store/slices/productSlice';
import {
  createSemiFinishedCategory,
  deleteSemiFinishedCategory,
  fetchSemiFinishedCategories,
  updateSemiFinishedCategory,
} from '../../store/slices/semiFinishedSlice';
import {
  createDishCategory,
  deleteDishCategory,
  fetchDishCategories,
  updateDishCategory,
} from '../../store/slices/dishSlice';

type CategoryKind = 'product' | 'semiFinished' | 'dish';

type CategoryForm = {
  name: string;
  description: string;
};

type CategoryItem = {
  id: number;
  name: string;
  description: string;
};
type CategorySort = 'name-asc' | 'name-desc' | 'description-asc' | 'description-desc';

const PAGE_SIZE = 15;
type ButtonTone = 'success' | 'info' | 'danger' | 'muted' | 'primary' | 'accent';

const buttonStyle = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : tone === 'accent' ? 'var(--crm-accent-contrast)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  padding: '8px 12px',
  cursor: 'pointer',
  ...extra,
});

const emptyForm: CategoryForm = {
  name: '',
  description: '',
};

const categoryKindOptions: { key: CategoryKind; label: string }[] = [
  { key: 'product', label: 'Категорії товарів' },
  { key: 'semiFinished', label: 'Категорії напівфабрикатів' },
  { key: 'dish', label: 'Категорії страв' },
];

const ProductCategoriesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const productState = useAppSelector((s) => s.product);
  const semiFinishedState = useAppSelector((s) => s.semiFinished);
  const dishState = useAppSelector((s) => s.dish);

  const [activeKind, setActiveKind] = useState<CategoryKind>('product');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CategoryForm>({ ...emptyForm });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<CategoryForm>({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<CategorySort>('name-asc');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchProductCategories());
    dispatch(fetchSemiFinishedCategories());
    dispatch(fetchDishCategories());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
    setEditingId(null);
    setEditForm({ ...emptyForm });
    setShowCreateForm(false);
    setCreateForm({ ...emptyForm });
    setSearchTerm('');
    setError(null);
  }, [activeKind]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getItemsByKind = (kind: CategoryKind): CategoryItem[] => {
    if (kind === 'product') return productState.categories;
    if (kind === 'semiFinished') return semiFinishedState.categories;
    return dishState.categories;
  };

  const getLoadingByKind = (kind: CategoryKind): boolean => {
    if (kind === 'product') return productState.loading;
    if (kind === 'semiFinished') return semiFinishedState.loading;
    return dishState.loading;
  };

  const currentItems = getItemsByKind(activeKind);
  const loading = getLoadingByKind(activeKind);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredItems = currentItems.filter((item) => {
    if (!normalizedSearch) return true;
    return item.name.toLowerCase().includes(normalizedSearch);
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'name-desc': return b.name.localeCompare(a.name, 'uk');
      case 'description-asc': return (a.description ?? '').localeCompare(b.description ?? '', 'uk');
      case 'description-desc': return (b.description ?? '').localeCompare(a.description ?? '', 'uk');
      case 'name-asc':
      default:
        return a.name.localeCompare(b.name, 'uk');
    }
  });
  const getSortArrow = (ascKey: CategorySort, descKey: CategorySort) => {
    if (sortBy === ascKey) return ' ↑';
    if (sortBy === descKey) return ' ↓';
    return '';
  };
  const toggleSort = (ascKey: CategorySort, descKey: CategorySort) => {
    setSortBy((prev) => (prev === ascKey ? descKey : ascKey));
  };

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pageItems = sortedItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageStart = sortedItems.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, sortedItems.length);

  const validateForm = (form: CategoryForm): string | null => {
    if (!form.name.trim()) return "Назва є обов'язковою";
    return null;
  };

  const resetCreateForm = () => {
    setCreateForm({ ...emptyForm });
    setError(null);
  };

  const resetInlineEdit = () => {
    setEditingId(null);
    setEditForm({ ...emptyForm });
    setError(null);
  };

  const refreshByKind = (kind: CategoryKind) => {
    if (kind === 'product') dispatch(fetchProductCategories());
    if (kind === 'semiFinished') dispatch(fetchSemiFinishedCategories());
    if (kind === 'dish') dispatch(fetchDishCategories());
  };

  const createByKind = async (kind: CategoryKind, data: CategoryForm) => {
    if (kind === 'product') await dispatch(createProductCategory(data));
    if (kind === 'semiFinished') await dispatch(createSemiFinishedCategory(data));
    if (kind === 'dish') await dispatch(createDishCategory(data));
  };

  const updateByKind = async (kind: CategoryKind, id: number, data: CategoryForm) => {
    if (kind === 'product') await dispatch(updateProductCategory({ id, data }));
    if (kind === 'semiFinished') await dispatch(updateSemiFinishedCategory({ id, data }));
    if (kind === 'dish') await dispatch(updateDishCategory({ id, data }));
  };

  const deleteByKind = async (kind: CategoryKind, id: number) => {
    if (kind === 'product') await dispatch(deleteProductCategory(id));
    if (kind === 'semiFinished') await dispatch(deleteSemiFinishedCategory(id));
    if (kind === 'dish') await dispatch(deleteDishCategory(id));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm(createForm);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await createByKind(activeKind, { name: createForm.name.trim(), description: createForm.description.trim() });
    resetCreateForm();
    refreshByKind(activeKind);
  };

  const handleInlineSave = async (id: number) => {
    const validationError = validateForm(editForm);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    await updateByKind(activeKind, id, { name: editForm.name.trim(), description: editForm.description.trim() });
    resetInlineEdit();
    refreshByKind(activeKind);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Видалити категорію?')) return;
    await deleteByKind(activeKind, id);
    if (editingId === id) resetInlineEdit();
    refreshByKind(activeKind);
  };

  const openEdit = (item: CategoryItem) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, description: item.description || '' });
    setShowCreateForm(false);
    setError(null);
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-md)',
    padding: '8px',
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text)',
  };

  const cellStyle: React.CSSProperties = { padding: '10px 8px', verticalAlign: 'top' };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>
        Управління категоріями
      </h2>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
        {categoryKindOptions.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setActiveKind(option.key)}
            style={buttonStyle(activeKind === option.key ? 'primary' : 'muted', { fontWeight: 600 })}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => {
            setShowCreateForm((prev) => !prev);
            resetInlineEdit();
            setError(null);
          }}
          style={buttonStyle(showCreateForm ? 'muted' : 'success', { padding: '8px 16px', fontWeight: 500 })}
        >
          {showCreateForm ? 'Сховати форму додавання' : '+ Показати форму додавання'}
        </button>

        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Пошук категорії..."
          style={{ ...inputStyle, maxWidth: '320px' }}
        />
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreateSubmit}
          className="product-category-create-form"
          style={{ marginTop: '12px', padding: '20px', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '8px', boxShadow: 'var(--crm-shadow-sm)' }}
        >
          <input
            style={inputStyle}
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            placeholder="Назва *"
          />
          <input
            style={inputStyle}
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            placeholder="Опис"
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={buttonStyle('success', { padding: '8px 14px', fontWeight: 600 })}>
              Створити
            </button>
            <button type="button" onClick={resetCreateForm} style={buttonStyle('muted', { padding: '8px 14px' })}>
              Очистити
            </button>
          </div>
        </form>
      )}

      {error && <div role="alert" style={{ marginTop: '10px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: '10px', borderRadius: '6px' }}>{error}</div>}

      {loading ? (
        <p style={{ marginTop: '20px' }}>Завантаження...</p>
      ) : (
        <>
          <div style={{ marginTop: '14px', fontSize: '13px', color: 'var(--crm-text-muted)' }}>
            Показано {pageStart}-{pageEnd} із {sortedItems.length} категорій
          </div>
          <div className="product-categories-table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: '8px', overflow: 'hidden', boxShadow: 'var(--crm-shadow-sm)', marginTop: '8px' }}>
            <thead style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' }}>
              <tr>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600' }}>#</th>
                <th
                  style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('name-asc', 'name-desc')}
                  title="Сортувати за назвою"
                >
                  Назва{getSortArrow('name-asc', 'name-desc')}
                </th>
                <th
                  style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('description-asc', 'description-desc')}
                  title="Сортувати за описом"
                >
                  Опис{getSortArrow('description-asc', 'description-desc')}
                </th>
                <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: '220px' }}>Дії</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item, i) => {
                const isEditing = editingId === item.id;
                return (
                  <tr key={item.id} style={{ background: i % 2 === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-surface)' }}>
                    <td style={cellStyle}>{item.id}</td>
                    <td style={cellStyle}>
                      {isEditing ? (
                        <input style={inputStyle} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                      ) : (
                        <strong>{item.name}</strong>
                      )}
                    </td>
                    <td style={cellStyle}>
                      {isEditing ? (
                        <input style={inputStyle} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                      ) : (
                        item.description || '—'
                      )}
                    </td>
                    <td style={cellStyle}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => handleInlineSave(item.id)} style={buttonStyle('success', { padding: '6px 10px' })}>Зберегти</button>
                          <button type="button" onClick={resetInlineEdit} style={buttonStyle('muted', { padding: '6px 10px' })}>Скасувати</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => openEdit(item)} style={buttonStyle('info', { padding: '6px 10px' })}>Ред.</button>
                          <button type="button" onClick={() => handleDelete(item.id)} style={buttonStyle('danger', { padding: '6px 10px' })}>Вид.</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                    Категорії не знайдені
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              style={buttonStyle(page <= 1 ? 'muted' : 'primary', { padding: '6px 12px', cursor: page <= 1 ? 'not-allowed' : 'pointer' })}
            >
              ← Назад
            </button>
            <span style={{ fontSize: '13px', color: 'var(--crm-text)' }}>Сторінка {page} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              style={buttonStyle(page >= totalPages ? 'muted' : 'primary', { padding: '6px 12px', cursor: page >= totalPages ? 'not-allowed' : 'pointer' })}
            >
              Вперед →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductCategoriesPage;
